import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Quote Job Additional - Create page.
 * Same Site section as main Estimating Create; Work Details = group6 only (Flooring Additional, Plastering, Other) + Summary.
 * Sets quoteType "Additional Works" and mainJobId on create.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { DateInput, PageHeader, PageHeaderWithBack, useToast, } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { useDeposit } from "@/context/DepositContext";
import { quotesService, } from "@/api/services/quotes.service";
import { sitesService } from "@/api/services/sites.service";
import { contactsService } from "@/api/services/contacts.service";
import { jobsService } from "@/api/services/jobs.service";
import { costsService } from "@/api/services/costs.service";
import { getContactDisplayName } from "@/utils/string";
import { isWeekend } from "@/utils/dateTime";
import { buildAddressLine } from "@/utils/address";
import { ADDITIONAL_WORKS_QUOTE_TYPE } from "@/utils/quoteHelpers";
import { getDefaultGroup6AccordionData, mergeGroup6FromApi, collectGroup6LineItems, computeGroup6ItemTotalCost, } from "./quoteJobAdditionalUtils";
import styles from "./EstimatingEdit.module.css";
const DEFAULT_AREA_INFO = [
    { label: "Tenancy Defit (m2)", value: 0 },
    { label: "Wall Finish (Plaster) (m2)", value: 0 },
    { label: "Wall Finish (Brickwork) (m2)", value: 0 },
    { label: "Shopfront (l/m)", value: 0 },
    { label: "Shopfront (under 3.6m) OR of Kiosk Lm (m2)", value: 0 },
];
const parseCurrency = (value) => {
    if (!value)
        return 0;
    const cleaned = String(value).replace(/[^0-9.-]/g, "");
    return parseFloat(cleaned) || 0;
};
const formatCurrency = (value) => `$${value.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const QuoteJobAdditionalCreate = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const { user } = useAuth();
    const { error: showError } = useToast();
    const siteIdParam = searchParams.get("siteId");
    const mainJobIdParam = searchParams.get("mainJobId");
    const [siteId, setSiteId] = useState("");
    const [contactId, setContactId] = useState("");
    const [clientName, setClientName] = useState("");
    const [quoteName, setQuoteName] = useState("");
    const [quoteDescription, setQuoteDescription] = useState("");
    const [commencementDate, setCommencementDate] = useState("");
    const [commencementTime, setCommencementTime] = useState("");
    const [completionDate, setCompletionDate] = useState("");
    const [completionTime, setCompletionTime] = useState("");
    const [projectDuration, setProjectDuration] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [loadingSite, setLoadingSite] = useState(false);
    const [loadedSiteData, setLoadedSiteData] = useState(null);
    const [sites, setSites] = useState([]);
    const companyDropdownRef = useRef(null);
    const attentionDropdownRef = useRef(null);
    const [companyNames, setCompanyNames] = useState([]);
    const [companyContactIdMap, setCompanyContactIdMap] = useState(new Map());
    const [loadingCompanyNames, setLoadingCompanyNames] = useState(false);
    const [companySearchTerm, setCompanySearchTerm] = useState("");
    const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
    const [selectedCompanyName, setSelectedCompanyName] = useState("");
    const [companyContacts, setCompanyContacts] = useState([]);
    const [attentionContactId, setAttentionContactId] = useState("");
    const [attentionSearchTerm, setAttentionSearchTerm] = useState("");
    const [showAttentionDropdown, setShowAttentionDropdown] = useState(false);
    const [selectedAttentionContactName, setSelectedAttentionContactName] = useState("");
    const [areaInfo, setAreaInfo] = useState(DEFAULT_AREA_INFO);
    const [accordionData, setAccordionData] = useState(getDefaultGroup6AccordionData);
    const [expandedAccordions, setExpandedAccordions] = useState(new Set());
    const buildersMarginEnabled = false;
    const buildersMarginPercentage = 10;
    const includeNewClientTerms = false;
    const depositEnabled = false;
    const { defaultDepositPercentage: depositPercentage } = useDeposit();
    const basePath = useMemo(() => (location.pathname.startsWith("/staff") ? "/staff" : "/admin"), [location.pathname]);
    const fetchSites = useCallback(async () => {
        try {
            const res = await sitesService.getSites({ pageSize: 100 });
            setSites(res.data.data.map((s) => ({
                id: s.id,
                name: s.siteName,
            })));
        }
        catch (err) {
            console.error("Error fetching sites:", err);
        }
    }, []);
    const fetchCompanyNames = useCallback(async () => {
        try {
            setLoadingCompanyNames(true);
            const res = await contactsService.getCompaniesWithContactIds();
            const data = res.data.data || [];
            const map = new Map();
            data.forEach((c) => {
                if (c.companyName && c.contactId)
                    map.set(c.companyName, c.contactId);
            });
            setCompanyContactIdMap(map);
            setCompanyNames(Array.from(map.keys()));
        }
        catch (err) {
            console.error("Error fetching companies:", err);
            setCompanyNames([]);
        }
        finally {
            setLoadingCompanyNames(false);
        }
    }, []);
    const handleCompanySelect = useCallback(async (companyName) => {
        setSelectedCompanyName(companyName);
        setCompanySearchTerm(companyName);
        setShowCompanyDropdown(false);
        setClientName(companyName);
        setContactId(companyContactIdMap.get(companyName) || "");
        setAttentionContactId("");
        setSelectedAttentionContactName("");
        setAttentionSearchTerm("");
        try {
            const res = await contactsService.getContacts({ companyName, page: 1 });
            const data = (res.data.data || []).map((c) => ({
                contactId: c.contactId,
                firstName: c.firstName,
                lastName: c.lastName,
                email: c.email ?? "",
                phone: c.phone,
                role: c.role,
            }));
            setCompanyContacts(data);
        }
        catch {
            setCompanyContacts([]);
        }
    }, [companyContactIdMap]);
    const handleClearCompany = useCallback(() => {
        setSelectedCompanyName("");
        setCompanySearchTerm("");
        setContactId("");
        setClientName("");
        setAttentionContactId("");
        setSelectedAttentionContactName("");
        setAttentionSearchTerm("");
        setCompanyContacts([]);
    }, []);
    const handleAttentionSelect = useCallback((contact) => {
        setAttentionContactId(contact.contactId);
        setSelectedAttentionContactName(getContactDisplayName(contact.firstName, contact.lastName, contact.email));
        setAttentionSearchTerm(getContactDisplayName(contact.firstName, contact.lastName, contact.email));
        setShowAttentionDropdown(false);
    }, []);
    const handleClearAttention = useCallback(() => {
        setAttentionContactId("");
        setSelectedAttentionContactName("");
        setAttentionSearchTerm("");
    }, []);
    useEffect(() => {
        if (siteIdParam) {
            setSiteId(siteIdParam);
        }
    }, [siteIdParam]);
    useEffect(() => {
        if (!mainJobIdParam)
            return;
        let cancelled = false;
        (async () => {
            try {
                const res = await jobsService.getJob(mainJobIdParam);
                const job = res.data;
                if (cancelled)
                    return;
                if (job.commencementDate)
                    setCommencementDate(job.commencementDate.slice(0, 10));
                if (job.startTime != null)
                    setCommencementTime(job.startTime);
                if (job.completionDate)
                    setCompletionDate(job.completionDate.slice(0, 10));
                if (job.endTime != null)
                    setCompletionTime(job.endTime);
                if (job.durationDays != null)
                    setProjectDuration(String(job.durationDays));
                if (job.siteId && !siteIdParam)
                    setSiteId(job.siteId);
                if (job.contactId) {
                    const contactRes = await contactsService.getContact(job.contactId);
                    if (cancelled || !contactRes.data)
                        return;
                    const contact = contactRes.data;
                    const company = contact.companyName ?? "";
                    setSelectedCompanyName(company);
                    setCompanySearchTerm(company);
                    setClientName(company);
                    setContactId(contact.contactId ?? "");
                    const displayName = getContactDisplayName(contact.firstName, contact.lastName, contact.email);
                    setAttentionContactId(contact.contactId ?? "");
                    setSelectedAttentionContactName(displayName);
                    setAttentionSearchTerm(displayName);
                    try {
                        const contactsRes = await contactsService.getContacts({
                            companyName: company,
                            page: 1,
                        });
                        const data = (contactsRes.data.data || []).map((c) => ({
                            contactId: c.contactId,
                            firstName: c.firstName,
                            lastName: c.lastName,
                            email: c.email ?? "",
                            phone: c.phone,
                            role: c.role,
                        }));
                        if (!cancelled)
                            setCompanyContacts(data);
                    }
                    catch {
                        if (!cancelled)
                            setCompanyContacts([]);
                    }
                }
            }
            catch {
                if (!cancelled) {
                    setCommencementDate("");
                    setCommencementTime("");
                    setCompletionDate("");
                    setCompletionTime("");
                    setProjectDuration("");
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [mainJobIdParam, siteIdParam]);
    useEffect(() => {
        fetchSites();
    }, [fetchSites]);
    useEffect(() => {
        const loadSite = async () => {
            if (!siteId)
                return;
            setLoadingSite(true);
            try {
                const res = await sitesService.getSite(siteId);
                const site = res.data;
                setLoadedSiteData(site);
                if (site.areaInfo?.length) {
                    const merged = DEFAULT_AREA_INFO.map((d) => {
                        const s = site.areaInfo?.find((a) => a.label === d.label);
                        return { ...d, value: s?.value ?? d.value };
                    });
                    setAreaInfo(merged);
                }
            }
            catch {
                setLoadedSiteData(null);
            }
            finally {
                setLoadingSite(false);
            }
        };
        loadSite();
    }, [siteId]);
    useEffect(() => {
        const loadGroup6 = async () => {
            try {
                const res = await costsService.getGroups();
                const groups = res.data || [];
                setAccordionData((prev) => {
                    const merged = mergeGroup6FromApi(prev, groups);
                    return merged.map((item) => ({
                        ...item,
                        sections: item.sections?.map((section) => ({
                            ...section,
                            items: section.items.map((sub) => {
                                if (!sub.inScope)
                                    return sub;
                                const computed = computeGroup6ItemTotalCost(sub, section, item, areaInfo, merged);
                                const newTotal = formatCurrency(computed);
                                return { ...sub, totalCost: newTotal };
                            }),
                        })),
                    }));
                });
            }
            catch (err) {
                console.error("Error loading group6:", err);
            }
        };
        loadGroup6();
    }, []);
    useEffect(() => {
        if (showCompanyDropdown)
            fetchCompanyNames();
    }, [showCompanyDropdown, fetchCompanyNames]);
    useEffect(() => {
        setAccordionData((prev) => {
            let hasChanges = false;
            const next = prev.map((item) => ({
                ...item,
                sections: item.sections?.map((section) => ({
                    ...section,
                    items: section.items.map((sub) => {
                        if (!sub.inScope)
                            return sub;
                        const computed = computeGroup6ItemTotalCost(sub, section, item, areaInfo, prev);
                        const newTotal = formatCurrency(computed);
                        if (newTotal !== (sub.totalCost || ""))
                            hasChanges = true;
                        return { ...sub, totalCost: newTotal };
                    }),
                })),
            }));
            return hasChanges ? next : prev;
        });
    }, [areaInfo]);
    const calculatedSummary = useMemo(() => {
        let subTotal = 0;
        accordionData.forEach((item) => {
            item.sections?.forEach((section) => {
                section.items.forEach((sub) => {
                    const isAfterHours = sub.title?.toLowerCase().includes("after hours");
                    if (sub.inScope && sub.totalCost && !isAfterHours)
                        subTotal += parseCurrency(sub.totalCost);
                });
            });
        });
        const marginAmount = buildersMarginEnabled
            ? (subTotal * buildersMarginPercentage) / 100
            : 0;
        const total = subTotal + marginAmount;
        const tenancyItem = areaInfo.find((i) => i.label.toLowerCase().includes("tenancy"));
        const tenancyArea = tenancyItem?.value || 0;
        const costPerM2 = tenancyArea > 0 ? total / tenancyArea : 0;
        const depositRaw = depositEnabled ? (total * depositPercentage) / 100 : 0;
        return {
            rawSubTotal: subTotal,
            rawTotal: total,
            rawCostPerM2: costPerM2,
            rawMarginAmount: marginAmount,
            subTotal: formatCurrency(subTotal),
            total: formatCurrency(total),
            costPerM2: formatCurrency(costPerM2),
            buildersMarginAmount: formatCurrency(marginAmount),
            depositAmount: formatCurrency(depositRaw),
        };
    }, [
        accordionData,
        buildersMarginEnabled,
        buildersMarginPercentage,
        areaInfo,
        depositEnabled,
        depositPercentage,
    ]);
    const updateSubItemField = useCallback((itemId, sectionIndex, subItemId, field, value) => {
        setAccordionData((prev) => {
            const itemIndex = prev.findIndex((i) => i.id === itemId);
            if (itemIndex === -1)
                return prev;
            const item = prev[itemIndex];
            const section = item.sections?.[sectionIndex];
            if (!section)
                return prev;
            const subItemIndex = section.items.findIndex((s) => s.id === subItemId);
            if (subItemIndex === -1)
                return prev;
            const updatedSection = {
                ...section,
                items: section.items.map((s, i) => i === subItemIndex ? { ...s, [field]: value } : s),
            };
            const updatedSubItem = updatedSection.items[subItemIndex];
            const isAfterHoursToggle = field === "inScope" &&
                updatedSubItem.title.toLowerCase().includes("after hours");
            // When After Hours is toggled, recalculate every item in this section.
            // Other items depend on the After Hours multiplier (unitCost × tenancyArea × factor).
            if (isAfterHoursToggle) {
                const sectionAfterToggle = {
                    ...updatedSection,
                    items: updatedSection.items.map((s, i) => {
                        if (i === subItemIndex) {
                            const needsDefault = value === true && (!s.unitCost || s.unitCost.trim() === "");
                            return needsDefault ? { ...s, unitCost: "1.3" } : s;
                        }
                        return s;
                    }),
                };
                const recalcedItems = sectionAfterToggle.items.map((s) => {
                    const isAH = s.title.toLowerCase().includes("after hours");
                    if (isAH) {
                        return { ...s, totalCost: "" };
                    }
                    if (!s.inScope) {
                        return { ...s, totalCost: "" };
                    }
                    const computed = computeGroup6ItemTotalCost(s, sectionAfterToggle, item, areaInfo, prev);
                    return { ...s, totalCost: formatCurrency(computed) };
                });
                const nextSection = { ...sectionAfterToggle, items: recalcedItems };
                const nextItem = {
                    ...item,
                    sections: (item.sections || []).map((sec, i) => i === sectionIndex ? nextSection : sec),
                };
                return prev.map((i, idx) => (idx === itemIndex ? nextItem : i));
            }
            const shouldRecalcTotal = field === "unitCost" ||
                field === "qty" ||
                (field === "inScope" && value === true);
            const shouldClearTotal = field === "inScope" && value === false;
            let newTotalCost;
            if (shouldClearTotal) {
                newTotalCost = "";
            }
            else if (shouldRecalcTotal && updatedSubItem.inScope) {
                const computed = computeGroup6ItemTotalCost(updatedSubItem, updatedSection, item, areaInfo, prev);
                newTotalCost = formatCurrency(computed);
            }
            const nextSection = newTotalCost !== undefined
                ? {
                    ...updatedSection,
                    items: updatedSection.items.map((s, i) => i === subItemIndex ? { ...s, totalCost: newTotalCost } : s),
                }
                : updatedSection;
            const nextItem = {
                ...item,
                sections: (item.sections || []).map((sec, i) => i === sectionIndex ? nextSection : sec),
            };
            return prev.map((i, idx) => (idx === itemIndex ? nextItem : i));
        });
    }, [areaInfo]);
    const toggleScope = useCallback((itemId, sectionIndex, subItemId) => {
        const item = accordionData.find((i) => i.id === itemId);
        const section = item?.sections?.[sectionIndex];
        const sub = section?.items.find((s) => s.id === subItemId);
        if (sub)
            updateSubItemField(itemId, sectionIndex, subItemId, "inScope", !sub.inScope);
    }, [accordionData, updateSubItemField]);
    const addOtherScope = useCallback(() => {
        const id = `other_custom_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
        setAccordionData((prev) => prev.map((o) => {
            if (o.id !== "other")
                return o;
            const sections = [...(o.sections || [])];
            const idx = sections.findIndex((s) => s.sectionId === "other_custom");
            const newItem = {
                id,
                title: "",
                inScope: true,
                qty: "",
                unitCost: "",
                totalCost: "",
                notes: "",
            };
            if (idx >= 0) {
                sections[idx] = {
                    ...sections[idx],
                    items: [...sections[idx].items, newItem],
                };
            }
            else {
                sections.push({
                    sectionId: "other_custom",
                    sectionTitle: "Manual Entries",
                    showQty: true,
                    items: [newItem],
                });
            }
            return { ...o, sections };
        }));
    }, []);
    const removeOtherScope = useCallback((sectionIndex, subItemId) => {
        setAccordionData((prev) => prev.map((o) => {
            if (o.id !== "other")
                return o;
            const sections = (o.sections || []).map((s, i) => {
                if (i !== sectionIndex)
                    return s;
                return {
                    ...s,
                    items: s.items.filter((item) => item.id !== subItemId),
                };
            });
            return { ...o, sections };
        }));
    }, []);
    const toggleAccordion = (id) => {
        setExpandedAccordions((prev) => {
            const next = new Set(prev);
            if (next.has(id))
                next.delete(id);
            else
                next.add(id);
            return next;
        });
    };
    const handleExpandAll = () => {
        setExpandedAccordions(new Set(accordionData.map((i) => i.id)));
    };
    const handleCollapseAll = () => setExpandedAccordions(new Set());
    const handleSave = async () => {
        if (!siteId?.trim()) {
            showError("Please select a site");
            return;
        }
        if (!selectedCompanyName?.trim()) {
            showError("Please enter a client name");
            return;
        }
        if (!attentionContactId?.trim()) {
            showError("Please select an Attention contact");
            return;
        }
        if (commencementDate && isWeekend(commencementDate)) {
            showError("Commencement date cannot fall on a weekend.");
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const lineItems = collectGroup6LineItems(accordionData);
            const address = loadedSiteData
                ? buildAddressLine({
                    address: loadedSiteData.address,
                    suburb: loadedSiteData.suburb,
                    state: loadedSiteData.state,
                    postcode: loadedSiteData.postcode,
                }) || undefined
                : undefined;
            const createData = {
                contactId,
                attentionContactId: attentionContactId || undefined,
                clientName: selectedCompanyName || clientName,
                siteId: siteId || undefined,
                title: quoteName,
                description: quoteDescription,
                status: "inprogress",
                commencementDate: commencementDate || undefined,
                commencementTime: commencementTime || undefined,
                completionDate: completionDate || undefined,
                completionTime: completionTime || undefined,
                projectDuration: projectDuration
                    ? parseInt(projectDuration, 10)
                    : undefined,
                areaInfo,
                lineItems,
                subTotal: calculatedSummary.rawSubTotal,
                totalAmount: calculatedSummary.rawTotal,
                costPerM2: calculatedSummary.rawCostPerM2,
                buildersMarginEnabled,
                buildersMarginPercentage,
                buildersMarginAmount: calculatedSummary.rawMarginAmount,
                includeNewClientPaymentTerms: includeNewClientTerms,
                depositEnabled,
                depositPercentage: depositEnabled ? depositPercentage : undefined,
                address,
                quoteType: ADDITIONAL_WORKS_QUOTE_TYPE,
                mainJobId: mainJobIdParam || undefined,
            };
            await quotesService.createQuote(createData);
            navigate(`${basePath}/estimating`);
        }
        catch (err) {
            const msg = err &&
                typeof err === "object" &&
                "response" in err &&
                err.response &&
                typeof err.response.data ===
                    "object"
                ? err.response.data?.message
                : null;
            setError(msg || "Failed to create quote");
            console.error("Error creating quote:", err);
        }
        finally {
            setSaving(false);
        }
    };
    const filteredAttentionContacts = useMemo(() => {
        if (!selectedCompanyName || !attentionSearchTerm.trim())
            return companyContacts;
        const term = attentionSearchTerm.toLowerCase();
        return companyContacts.filter((c) => {
            const name = `${c.firstName} ${c.lastName}`.toLowerCase();
            const email = (c.email || "").toLowerCase();
            return name.includes(term) || email.includes(term);
        });
    }, [companyContacts, attentionSearchTerm, selectedCompanyName]);
    const renderWorkContent = () => (_jsxs(_Fragment, { children: [_jsx("div", { className: styles.accordionList, children: accordionData.map((item) => (_jsxs("div", { className: styles.accordionItem, children: [_jsx("button", { className: `${styles.accordionHeader} ${expandedAccordions.has(item.id) ? styles.expanded : ""}`, onClick: () => toggleAccordion(item.id), children: _jsxs("div", { className: styles.accordionTitleSection, children: [_jsx("svg", { className: styles.accordionChevron, width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: _jsx("path", { d: "M6 9l6 6 6-6" }) }), _jsx("span", { className: styles.accordionTitle, children: item.title })] }) }), expandedAccordions.has(item.id) && item.sections && (_jsxs("div", { className: styles.accordionBody, children: [item.id === "other" && (_jsx("div", { className: styles.accordionControls, style: { marginBottom: 8 }, children: _jsx("button", { type: "button", className: styles.controlBtn, onClick: addOtherScope, children: "Add scope" }) })), _jsx("div", { className: styles.subItemsTable, children: item.sections.map((section, sectionIndex) => (_jsxs("div", { className: styles.sectionBlock, children: [section.sectionTitle && (_jsx("div", { className: styles.sectionTitleRow, children: _jsx("span", { className: styles.sectionTitleText, children: section.sectionTitle }) })), _jsxs("div", { className: section.sectionId === "other_custom"
                                                    ? styles.tableHeaderWithQtyAndAction
                                                    : section.showQty
                                                        ? styles.tableHeaderWithQty
                                                        : styles.tableHeader, children: [_jsx("div", { className: styles.tableHeaderCell }), _jsx("div", { className: styles.tableHeaderCell, children: "In Scope" }), section.showQty && (_jsx("div", { className: styles.tableHeaderCell, children: "Qty" })), _jsx("div", { className: styles.tableHeaderCell, children: "Unit Cost" }), _jsx("div", { className: styles.tableHeaderCell, children: "Total Cost" }), _jsx("div", { className: styles.tableHeaderCell, children: "Notes" }), section.sectionId === "other_custom" && (_jsx("div", { className: styles.tableHeaderCell }))] }), section.items.map((subItem) => {
                                                const isManual = section.sectionId === "other_custom";
                                                const isAfterHours = subItem.title
                                                    .toLowerCase()
                                                    .includes("after hours");
                                                return (_jsxs("div", { className: section.sectionId === "other_custom"
                                                        ? styles.tableRowWithQtyAndAction
                                                        : section.showQty
                                                            ? styles.tableRowWithQty
                                                            : styles.tableRow, children: [_jsx("div", { className: styles.tableCell, children: isManual ? (_jsx("input", { type: "text", className: styles.notesInput, placeholder: "Enter description", value: subItem.title, onChange: (e) => updateSubItemField(item.id, sectionIndex, subItem.id, "title", e.target.value) })) : (_jsx("span", { className: styles.subItemTitle, children: subItem.title })) }), _jsx("div", { className: styles.tableCell, children: _jsx("button", { className: `${styles.toggleButton} ${subItem.inScope ? styles.yes : styles.no}`, onClick: () => toggleScope(item.id, sectionIndex, subItem.id), children: subItem.inScope ? "YES" : "NO" }) }), section.showQty && (_jsx("div", { className: styles.tableCell, children: _jsx("input", { type: "text", className: styles.qtyInput, value: subItem.qty || "", onChange: (e) => updateSubItemField(item.id, sectionIndex, subItem.id, "qty", e.target.value) }) })), _jsx("div", { className: styles.tableCell, children: _jsx("input", { type: "text", className: styles.costInput, value: subItem.unitCost || "", onChange: (e) => updateSubItemField(item.id, sectionIndex, subItem.id, "unitCost", e.target.value) }) }), isAfterHours ? (_jsx("div", { className: styles.tableCell })) : (_jsx("div", { className: styles.tableCell, children: _jsx("input", { type: "text", className: styles.costInput, value: subItem.totalCost || "", onChange: (e) => updateSubItemField(item.id, sectionIndex, subItem.id, "totalCost", e.target.value) }) })), !isAfterHours && (_jsx("div", { className: styles.tableCell, children: _jsx("input", { type: "text", className: styles.notesInput, value: subItem.notes || "", onChange: (e) => updateSubItemField(item.id, sectionIndex, subItem.id, "notes", e.target.value) }) })), isManual && (_jsx("div", { className: styles.tableCell, children: _jsx("button", { type: "button", className: styles.deleteRowBtn, title: "Remove", onClick: () => removeOtherScope(sectionIndex, subItem.id), children: "\u00D7" }) }))] }, subItem.id));
                                            })] }, section.sectionId))) })] }))] }, item.id))) }), _jsx("div", { className: styles.summaryContainer + " " + styles.summaryContainerAdditionalWorks, children: _jsxs("div", { className: styles.summaryRow, children: [_jsx("div", { className: styles.summaryLabel, children: "Total" }), _jsx("div", { className: styles.summaryValue, children: calculatedSummary.total })] }) })] }));
    return (_jsx(_Fragment, { children: _jsxs("div", { className: styles.container, children: [_jsx(PageHeader, { userName: user?.name || "User", userEmail: user?.email || "" }), _jsx("div", { className: styles.header, children: _jsx(PageHeaderWithBack, { title: "Create Additional Works Quote", onBack: () => navigate(-1), breadcrumb: `Estimating / Additional Works` }) }), error && (_jsx("div", { className: styles.errorBanner, children: _jsx("p", { children: error }) })), _jsx("div", { className: styles.formSectionCreateStyle, children: _jsxs("div", { className: styles.formGridCreateStyle, children: [_jsxs("div", { className: styles.leftColumnCreateStyle, children: [_jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Site *" }), _jsxs("select", { className: "form-select", value: siteId, onChange: (e) => setSiteId(e.target.value), disabled: loadingSite || !!siteIdParam, children: [_jsx("option", { value: "", children: "Select Site" }), sites.map((s) => (_jsx("option", { value: s.id, children: s.name }, s.id)))] })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Client Name *" }), _jsxs("div", { className: styles.contactDropdownWrapper, ref: companyDropdownRef, children: [_jsxs("div", { className: styles.contactInputWrapper, children: [_jsx("input", { type: "text", className: "form-input", placeholder: "Type to search", value: companySearchTerm, onChange: (e) => {
                                                                    setCompanySearchTerm(e.target.value);
                                                                    setShowCompanyDropdown(true);
                                                                }, onFocus: () => setShowCompanyDropdown(true) }), selectedCompanyName && (_jsx("button", { type: "button", className: styles.clearBtn, onClick: handleClearCompany, title: "Clear", children: "\u00D7" }))] }), showCompanyDropdown && (_jsx("div", { className: styles.contactDropdownMenu, children: loadingCompanyNames ? (_jsx("div", { className: styles.dropdownLoading, children: "Loading..." })) : (companyNames.slice(0, 50).map((name) => (_jsx("div", { className: styles.contactOption, onClick: () => handleCompanySelect(name), children: name }, name)))) }))] })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Attention *" }), _jsxs("div", { className: styles.contactDropdownWrapper, ref: attentionDropdownRef, children: [_jsxs("div", { className: styles.contactInputWrapper, children: [_jsx("input", { type: "text", className: "form-input", placeholder: !selectedCompanyName
                                                                    ? "Select company first"
                                                                    : "Type to search", value: attentionSearchTerm, onChange: (e) => {
                                                                    setAttentionSearchTerm(e.target.value);
                                                                    setShowAttentionDropdown(true);
                                                                }, onFocus: () => selectedCompanyName && setShowAttentionDropdown(true), disabled: !selectedCompanyName }), selectedAttentionContactName && (_jsx("button", { type: "button", className: styles.clearBtn, onClick: handleClearAttention, title: "Clear", children: "\u00D7" }))] }), showAttentionDropdown && selectedCompanyName && (_jsx("div", { className: styles.contactDropdownMenu, children: filteredAttentionContacts.length > 0 ? (filteredAttentionContacts.map((c) => (_jsx("div", { className: styles.contactOption, onClick: () => handleAttentionSelect(c), children: getContactDisplayName(c.firstName, c.lastName, c.email) }, c.contactId)))) : (_jsx("div", { className: styles.dropdownEmpty, children: "No contacts found" })) }))] })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Quote Name" }), _jsx("input", { type: "text", className: "form-input", value: quoteName, onChange: (e) => setQuoteName(e.target.value) })] }), _jsxs("div", { className: `${styles.formGroupCol} form-group-col`, children: [_jsx("label", { className: "form-label", children: "Description" }), _jsx("textarea", { className: "form-textarea", value: quoteDescription, onChange: (e) => setQuoteDescription(e.target.value), rows: 3 })] })] }), _jsxs("div", { className: styles.rightColumnCreateStyle, children: [_jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Commencement Date" }), _jsx(DateInput, { className: "form-input", value: commencementDate, onChange: setCommencementDate, onInvalid: () => showError("Please enter a valid date.") })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Commencement Time" }), _jsx("input", { type: "time", className: "form-input", value: commencementTime, onChange: (e) => setCommencementTime(e.target.value) })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Completion Date" }), _jsx(DateInput, { className: "form-input", value: completionDate, onChange: setCompletionDate, onInvalid: () => showError("Please enter a valid date.") })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Completion Time" }), _jsx("input", { type: "time", className: "form-input", value: completionTime, onChange: (e) => setCompletionTime(e.target.value) })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Project Duration (days)" }), _jsx("input", { type: "text", className: "form-input", value: projectDuration, onChange: (e) => setProjectDuration(e.target.value) })] }), _jsxs("div", { children: [_jsx("h3", { className: styles.sectionTitle, children: "AREA INFORMATION" }), _jsx("div", { className: styles.areaInfoGrid, children: areaInfo.map((item, index) => (_jsxs("div", { className: styles.areaInfoRow, children: [_jsx("label", { className: styles.areaLabel, children: item.label }), _jsx("input", { type: "number", className: styles.areaInput, value: item.value, onFocus: (e) => {
                                                                if (item.value === 0)
                                                                    e.target.select();
                                                            }, onChange: (e) => {
                                                                const next = [...areaInfo];
                                                                next[index] = {
                                                                    ...next[index],
                                                                    value: Number(e.target.value),
                                                                };
                                                                setAreaInfo(next);
                                                            } })] }, index))) })] })] })] }) }), _jsxs("div", { className: styles.workDetailsSection, children: [_jsx("h3", { className: styles.workDetailsTitle, children: "Work Details (Additional)" }), _jsxs("div", { className: styles.accordionControls, children: [_jsx("button", { type: "button", className: styles.controlBtn, onClick: handleExpandAll, children: "Expand All" }), _jsx("button", { type: "button", className: styles.controlBtn, onClick: handleCollapseAll, children: "Collapse All" })] }), _jsx("div", { className: styles.accordionContainer, children: renderWorkContent() })] }), _jsx("div", { className: styles.globalActionFooter, children: _jsx("button", { className: styles.saveEstimateButton, onClick: handleSave, disabled: saving, children: saving ? "Creating..." : "Create Additional Works Quote" }) })] }) }));
};
